/**
 * Sprint PERMUTA-UX (PARTE A.3) — VenditaPicker
 *
 * Selettore con ricerca per scegliere una vendita/pratica (sostituisce
 * window.prompt per "seleziona pratica origine" nella card Carico veicolo).
 *
 * Mostra solo pratiche (tipo=pratica) per default (la permuta proviene
 * da una vendita di un altro veicolo). Backend filtra per scope.
 *
 * Props:
 *  - open, onOpenChange
 *  - onSelect(vendita): callback con la vendita scelta
 *  - excludeId, contextCompanyId
 *  - testIdPrefix
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";

const STATUS_LABEL = {
  draft: "Bozza",
  impegnata: "Impegnata",
  venduta: "Venduta",
  consegnata: "Consegnata",
  annullata: "Annullata",
};

export default function VenditaPicker({
  open, onOpenChange, onSelect,
  excludeId = null, contextCompanyId = null,
  testIdPrefix = "vendita-picker",
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setQ(""); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams();
    params.set("tipo", "pratica");
    if (q.trim()) params.set("search", q.trim());
    if (contextCompanyId) params.set("company_id", contextCompanyId);
    params.set("page_size", "60");
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/vendite?${params.toString()}`)
        .then((r) => {
          const all = r.data?.items || [];
          setItems(all.filter((v) => v.id !== excludeId));
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, excludeId, contextCompanyId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Scegli pratica origine</DialogTitle>
          <DialogDescription>
            Cerca la pratica/vendita che ha generato questa permuta.
            Filtro per codice pratica, cliente, marca/modello del veicolo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Codice pratica, cliente, veicolo..."
                   className="pl-8" autoFocus
                   data-testid={`${testIdPrefix}-search-input`} />
          </div>
          <div className="border border-slate-200 rounded-md max-h-[420px] overflow-y-auto divide-y">
            {loading ? (
              <div className="p-6 text-center text-slate-400 italic">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-slate-400 italic">Nessuna pratica trovata</div>
            ) : items.map((v) => {
              const snap = v.vehicle_snapshot || {};
              const cli = v.cliente || {};
              const cliName = cli.ragione_sociale
                || `${cli.cognome || ""} ${cli.nome || ""}`.trim()
                || "—";
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onSelect(v)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50/50 flex items-center justify-between gap-3"
                  data-testid={`${testIdPrefix}-result-${v.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-blue-700">{v.codice}</span>
                      <span className="font-medium truncate">{cliName}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 flex gap-3 mt-0.5">
                      <span>{snap.marca || ""} {snap.modello || ""}</span>
                      <span className="font-mono">{snap.targa || "—"}</span>
                      <span>{v.data_impegno || v.data_contratto || "—"}</span>
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                    {STATUS_LABEL[v.status] || v.status}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
